import { useMemo } from 'react';
import { listShapes, type ShapeDefinition } from '@/core/shapes';

/**
 * Panel shape picker.
 *
 * The icons are drawn from each shape's own `outline`, so a newly registered
 * shape gets a correct thumbnail without anyone drawing one. Shapes that
 * alternate orientation show both states, which is the clearest way to say
 * that a triangle grid is made of two mirrored parts.
 */

interface ShapePickerProps {
  value: string;
  onChange(id: string): void;
}

function outlinePath(shape: ShapeDefinition, rotationDegrees = 0): string {
  const outline = shape.outline(2, 0);
  const angle = (rotationDegrees * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  return (
    outline
      .map((point, index) => {
        const x = point.x * cos - point.y * sin;
        // SVG's y axis points down while panel space points up.
        const y = -(point.x * sin + point.y * cos);
        return `${index === 0 ? 'M' : 'L'}${x.toFixed(3)} ${y.toFixed(3)}`;
      })
      .join(' ') + ' Z'
  );
}

export function ShapePicker({ value, onChange }: ShapePickerProps) {
  const shapes = useMemo(() => listShapes(), []);
  const active = shapes.find((shape) => shape.id === value);

  return (
    <div className="field">
      <span className="field__label">Panel shape</span>

      <div className="shape-picker" role="radiogroup" aria-label="Panel shape">
        {shapes.map((shape) => {
          const alternates = shape.orientation?.(1, 0) !== shape.orientation?.(0, 0);
          return (
            <button
              key={shape.id}
              type="button"
              role="radio"
              aria-checked={shape.id === value}
              className={`shape-chip${shape.id === value ? ' is-active' : ''}`}
              title={shape.description}
              onClick={() => onChange(shape.id)}
            >
              <svg className="shape-chip__icon" viewBox="-1.4 -1.4 2.8 2.8" aria-hidden="true">
                <path d={outlinePath(shape)} />
                {alternates ? (
                  <path className="shape-chip__ghost" d={outlinePath(shape, 180)} />
                ) : null}
              </svg>
              <span className="shape-chip__name">{shape.name}</span>
            </button>
          );
        })}
      </div>

      {active ? (
        <p className="field__help">
          {active.description}
          {active.gridHint ? ` ${active.gridHint}` : ''}
        </p>
      ) : null}
    </div>
  );
}
